import { DownloadIcon } from '@radix-ui/react-icons'
import { Container, Flex, Heading, Section, Skeleton, Table, Text } from '@radix-ui/themes'
import { useCallback, useEffect, useMemo } from 'react'
import './styles.scss'
import { useMutation } from '@tanstack/react-query'
import { BackOfficeDataServiceAPI } from '@API/data-services/back-office-data-service'
import {
  convertDateToIsoDate,
  formatDateToMonthDayYearString,
  getNearestDayOfWeek,
} from '@Components/util/utils'
import { addWeeks, isSaturday, isSunday } from 'date-fns'
import { RootState } from '@AppState/redux-store'
import { useSelector } from 'react-redux'

type ReportDownload = {
  label: string
  fetchReportUrl: (startDate: string, endDate: string) => Promise<string>
}

type WeeklyReportProps = {
  title: string
  fetchReportUrl?: (startDate: string, endDate: string) => Promise<string>
  downloads?: ReportDownload[]
}

export const WeeklyReport = ({ title, fetchReportUrl, downloads }: WeeklyReportProps) => {
  const { activeCustomerMetadata } = useSelector((state: RootState) => state.users)

  const resolvedDownloads: ReportDownload[] = useMemo(
    () => downloads ?? (fetchReportUrl ? [{ label: 'CSV', fetchReportUrl }] : []),
    [downloads, fetchReportUrl],
  )

  const { mutate: fetchReport, data: reportUrl } = useMutation({
    mutationFn: ({ startDate, endDate }: { startDate: string; endDate: string }) => {
      return resolvedDownloads[0].fetchReportUrl(startDate, endDate)
    },
  })

  useEffect(() => {
    const downloadFile = async (reportUrl: string) => {
      const url = `${BackOfficeDataServiceAPI.getBackOfficeConfig().basePath}${reportUrl}`
      const a = document.createElement('a')
      a.href = url
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 0)
    }

    if (reportUrl) {
      downloadFile(reportUrl)
    }
  }, [reportUrl])

  const handleDownload = useCallback(
    async (
      fetchFn: (startDate: string, endDate: string) => Promise<string>,
      start: Date,
      end: Date,
    ) => {
      const startDate = convertDateToIsoDate(start)
      const endDate = convertDateToIsoDate(end)
      const urlPath = await fetchFn(startDate, endDate)
      const url = `${BackOfficeDataServiceAPI.getBackOfficeConfig().basePath}${urlPath}`
      const a = document.createElement('a')
      a.href = url
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 0)
    },
    [],
  )

  const mapReportRows = useCallback(() => {
    return new Array(5).fill(new Date()).map((date, i) => {
      const currentDate = addWeeks(date, -i)
      const start = getNearestDayOfWeek(currentDate, isSunday, false)
      const end = getNearestDayOfWeek(currentDate, isSaturday, true)

      return (
        <Table.Row key={i}>
          <Table.RowHeaderCell className="report-description-cell">
            <Text>
              Weekly Report (
              {formatDateToMonthDayYearString(start, activeCustomerMetadata?.timezone)} -{' '}
              {formatDateToMonthDayYearString(end, activeCustomerMetadata?.timezone)})
            </Text>
          </Table.RowHeaderCell>
          <Table.Cell align="right">
            <Flex className="download-cell-container" justify="end" gap="4">
              {resolvedDownloads.length === 1 ? (
                <Flex
                  className="download-cell"
                  align="center"
                  direction="column"
                  onClick={async () => {
                    fetchReport({
                      startDate: convertDateToIsoDate(start),
                      endDate: convertDateToIsoDate(end),
                    })
                  }}>
                  <DownloadIcon />
                  <Text size="1">CSV</Text>
                </Flex>
              ) : (
                resolvedDownloads.map((download) => (
                  <Flex
                    key={download.label}
                    className="download-cell"
                    align="center"
                    direction="column"
                    onClick={() => handleDownload(download.fetchReportUrl, start, end)}>
                    <DownloadIcon />
                    <Text size="1">{download.label}</Text>
                  </Flex>
                ))
              )}
            </Flex>
          </Table.Cell>
        </Table.Row>
      )
    })
  }, [activeCustomerMetadata?.timezone, fetchReport, resolvedDownloads, handleDownload])

  return (
    <Container>
      <Section>
        <Heading>{title}</Heading>
      </Section>

      <Skeleton loading={false}>
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Time Period</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell align="right">Download</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>{mapReportRows()}</Table.Body>
        </Table.Root>
      </Skeleton>
    </Container>
  )
}
